import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import { AdBanner } from "@/components/app/AdBanner";
import ShareReportDialog from "@/components/app/ShareReportDialog";
import { Button } from "@/components/ui/button";
import { Users, ClipboardCheck, Clock, GraduationCap, AlertTriangle, Share2 } from "lucide-react";

const CATEGORY_LABELS = {
  late_coming: "Late Coming",
  no_id_card: "No ID Card",
  improper_grooming: "Improper Grooming",
  no_homework: "No Homework",
  forgot_diary: "Forgot Diary",
  mobile_phone: "Mobile Phone",
  misbehavior: "Misbehavior",
  other: "Other",
};

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-carmel-navy/10 rounded-lg p-5 shadow-card animate-fade-in-up">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-carmel-navy/50">{label}</p>
          <p className="font-serif text-2xl md:text-4xl tracking-tight text-carmel-navy mt-2">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  return (
    <AppShell active="/">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">Today at a glance</p>
          <h2 className="font-serif text-2xl md:text-4xl tracking-tight text-carmel-navy">Daily Discipline Overview</h2>
          <p className="text-sm text-carmel-navy/60 mt-2 max-w-2xl">
            A quick pulse on today's inspection activity. Share the report instantly to the principal via WhatsApp or email.
          </p>
        </div>
        <Button
          onClick={() => setShareOpen(true)}
          className="bg-carmel-gold hover:bg-carmel-goldDark"
          data-testid="share-report-button"
        >
          <Share2 className="w-4 h-4 mr-2" /> Share Report
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <Stat label="Enrolled Students" value={stats?.total_students ?? "—"} icon={GraduationCap} accent="bg-carmel-navy/5 text-carmel-navy" />
        <Stat label="Today Inspections" value={stats?.today_inspections ?? "—"} icon={ClipboardCheck} accent="bg-carmel-gold/10 text-carmel-gold" />
        <Stat label="All-Time Records" value={stats?.total_inspections ?? "—"} icon={Clock} accent="bg-blue-50 text-blue-700" />
        <Stat label="Categories Flagged Today" value={Object.keys(stats?.today_by_category || {}).length} icon={AlertTriangle} accent="bg-red-50 text-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white border border-carmel-navy/10 rounded-lg p-6 shadow-card">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-serif text-2xl tracking-tight">Today by Category</h3>
            <span className="text-[11px] uppercase tracking-widest text-carmel-navy/40">Live</span>
          </div>
          <div className="space-y-3">
            {Object.entries(CATEGORY_LABELS).map(([k, label]) => {
              const c = stats?.today_by_category?.[k] || 0;
              const total = stats?.today_inspections || 1;
              const pct = Math.round((c / Math.max(total, 1)) * 100);
              return (
                <div key={k} data-testid={`cat-row-${k}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-carmel-navy/80">{label}</span>
                    <span className="font-medium tabular-nums">{c}</span>
                  </div>
                  <div className="h-1.5 bg-carmel-navy/5 rounded-full overflow-hidden">
                    <div className="h-full bg-carmel-gold transition-[width] duration-500" style={{ width: `${c ? pct : 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-carmel-navy/10 rounded-lg p-6 shadow-card">
          <h3 className="font-serif text-2xl tracking-tight mb-4">Most Flagged Classes Today</h3>
          {(!stats?.today_top_classes || stats.today_top_classes.length === 0) ? (
            <p className="text-sm text-carmel-navy/50 py-8 text-center">No inspections recorded today yet.</p>
          ) : (
            <div className="space-y-2">
              {stats.today_top_classes.map((c, idx) => (
                <div key={c.class} className="flex items-center justify-between py-2 border-b last:border-0 border-carmel-navy/5" data-testid={`top-class-${c.class}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl text-carmel-gold w-6">{idx + 1}</span>
                    <span className="font-medium">{c.class}</span>
                  </div>
                  <span className="text-sm text-carmel-navy/60 tabular-nums">{c.count} record{c.count !== 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <AdBanner className="min-h-[90px]" />
      </div>

      <ShareReportDialog open={shareOpen} onOpenChange={setShareOpen} />
    </AppShell>
  );
}
